import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaWhatsapp, 
  FaRocket, 
  FaLaptopCode, 
  FaGraduationCap, 
  FaArrowRight,
  FaChild,
  FaSchool,
  FaTrophy,
  FaHome,
  FaChalkboardTeacher,
  FaCode,
  FaMedal,
  FaUsers,
  FaCalendarAlt,
  FaStar,
  FaRobot,
  FaPlay,
  FaCogs,
  FaLightbulb,
  FaGlobe,
  FaChevronDown
} from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import PDFNewsletterPopup from './PDFNewsletterPopup';
import './HomePage.css';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [activeCounter, setActiveCounter] = useState({});
  
  // Animation on scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 100);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Counter animation
  useEffect(() => {
    const animateCounter = (targetId, endValue, duration = 2000) => {
      let startValue = 0;
      const increment = endValue / (duration / 50);
      const timer = setInterval(() => {
        startValue += increment;
        if (startValue >= endValue) {
          clearInterval(timer);
          startValue = endValue;
        }
        setActiveCounter(prev => ({ ...prev, [targetId]: Math.floor(startValue) }));
      }, 50);
    };

    // Trigger counters with intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter('students', 10000);
            animateCounter('schools', 50);
            animateCounter('competitions', 25);
            animateCounter('awards', 15);
          }
        });
      },
      { threshold: 0.5 }
    );

    const statsSection = document.querySelector('.stats-section');
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, []);

  // Programs data
  const programs = [
    {
      icon: <FaChild />,
      title: "Robotics & Coding Classes",
      description: "Interactive learning starting from age 4+",
      ageRange: "Ages 4+",
      highlight: "Starting from 4 years!"
    },
    {
      icon: <FaSchool />,
      title: "After-School Programs",
      description: "Weekend & after-school tech programs",
      ageRange: "All ages",
      highlight: "Flexible timing"
    },
    {
      icon: <FaCalendarAlt />,
      title: "Holiday STEM Camps",
      description: "Intensive hands-on learning during holidays",
      ageRange: "Ages 4-17",
      highlight: "Fun & educational"
    },
    {
      icon: <FaHome />,
      title: "Homeschool Support",
      description: "Robotics & coding for homeschoolers",
      ageRange: "Customized",
      highlight: "Personalized learning"
    },
    {
      icon: <FaTrophy />,
      title: "Robotics Competitions",
      description: "National & international competitions",
      ageRange: "Ages 4+",
      highlight: "Global recognition"
    },
    {
      icon: <FaChalkboardTeacher />,
      title: "Teacher Training",
      description: "School STEM integration & teacher support",
      ageRange: "Educators",
      highlight: "Professional development"
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      name: "David Gideon Kariuki HSC",
      role: "Merishaw School Principal",
      text: "Robots make creativity easier and show our children are conquering the world!",
      rating: 5
    },
    {
      id: 2,
      name: "Sarah Kimani",
      role: "Parent of 8-year-old",
      text: "My daughter's confidence in STEM subjects has grown tremendously since joining Stemtrix. She's now considering engineering as a career!",
      rating: 5
    },
    {
      id: 3,
      name: "Isaac Arabu",
      role: "Robotics Coach",
      text: "The curriculum and materials provided by Stemtrix make teaching robotics accessible and exciting for both educators and students.",
      rating: 5
    },
    {
      id: 4,
      name: "Mary Wanjiku",
      role: "Homeschool Mom",
      text: "Stemtrix has been a game-changer for our homeschooling journey. My kids love building and programming their robots!",
      rating: 5
    }
  ];

  // Age groups data
  const ageGroups = [
    {
      title: "Little Builders",
      age: "4-5 years",
      description: "Introduction to basic robotics concepts through play",
      features: ["Block-based programming", "Simple robot building", "Motor skills development"]
    },
    {
      title: "Young Engineers",
      age: "6-9 years",
      description: "Hands-on robot construction and basic coding",
      features: ["Robot assembly", "Visual programming", "Problem-solving games"]
    },
    {
      title: "Tech Innovators",
      age: "10-13 years",
      description: "Advanced robotics and competition preparation",
      features: ["Complex builds", "Text-based coding", "Competition participation"]
    },
    {
      title: "Future Leaders",
      age: "14+ years",
      description: "Professional-level robotics and AI concepts",
      features: ["AI integration", "Advanced programming", "Leadership roles"]
    }
  ];

  // Stats data
  const stats = [
    { id: 'students', label: 'Students Trained', value: 10000, suffix: '+' },
    { id: 'schools', label: 'Partner Schools', value: 50, suffix: '+' },
    { id: 'competitions', label: 'Organized Competitions', value: 25, suffix: '+' },
    { id: 'awards', label: 'International Awards', value: 15, suffix: '+' }
  ];

  return (
    <>
      <Helmet>
        <title>Stemtrix East Africa - Leading Robotics & STEM Education for Kids Age 4+ | Nairobi, Kenya</title>
        <meta
          name="description"
          content="Transform your child's future with Stemtrix East Africa's innovative robotics and STEM education. Starting from age 4+. International competitions, coding classes, and hands-on learning in Nairobi, Kenya."
        />
        <meta
          name="keywords"
          content="robotics education Kenya, STEM classes Nairobi, coding for kids age 4, robotics competitions Kenya, after school programs, holiday STEM camps, homeschool robotics, teacher training STEM, robotics kits Kenya, children programming classes, Stemtrix East Africa"
        />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://stemtrix.co.ke" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Stemtrix East Africa - Leading Robotics & STEM Education for Kids Age 4+" />
        <meta property="og:description" content="Transform your child's future with innovative robotics and STEM education. International competitions and hands-on learning starting from age 4!" />
        <meta property="og:image" content="https://stemtrix.co.ke/images/stemtrix-robotics-kids.jpg" />
        <meta property="og:url" content="https://stemtrix.co.ke" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Stemtrix East Africa - Leading Robotics & STEM Education" />
        <meta name="twitter:description" content="Transform your child's future with innovative robotics and STEM education." />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Stemtrix East Africa",
            "url": "https://stemtrix.co.ke",
            "description": "Leading STEM education and robotics learning for children ages 4+ in East Africa",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "Kenya",
              "addressLocality": "Nairobi"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+254728128353",
              "contactType": "customer support"
            }
          })}
        </script>
      </Helmet>
      
      <PDFNewsletterPopup />

      <main className="home">
        {/* Modern Hero Section */}
        <section className="hero-section">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-particles">
              {[...Array(20)].map((_, i) => (
                <div key={i} className={`particle particle-${i + 1}`}></div>
              ))}
            </div>
          </div>
          
          <Container>
            <Row className="align-items-center min-vh-100">
              <Col lg={6} className="hero-content">
                <div className="hero-badge">
                  <FaRobot className="me-2" />
                  <span>Leading STEM Education in East Africa</span>
                </div>
                
                <h1 className="hero-title">
                  Inspiring Tomorrow's
                  <span className="gradient-text"> Innovators </span>
                  Today
                </h1>
                
                <p className="hero-description">
Give your child a head start in the future of technology. From age 4+, we build innovation and problem-solving skills through robotics, coding, hands-on learning, and international competitions.
                </p>
                
                <div className="hero-stats">
                  <div className="stat-item">
                    <strong>10,000+</strong>
                    <span>Students Trained</span>
                  </div>
                  <div className="stat-item">
                    <strong>50+</strong>
                    <span>Partner Schools</span>
                  </div>
                  <div className="stat-item">
                    <strong>25+</strong>
                    <span>Organized Competition</span>
                  </div>
                </div>
                
                <div className="hero-cta">
                  <Link to="/join-us" className="cta-btn cta-primary">
                    <FaRocket className="me-2" />
                   Join Our STEM Learning Program
                    <FaArrowRight className="ms-2" />
                  </Link>
                  <Link to="/tournament" className="cta-btn cta-secondary">
                    <FaTrophy className="me-2" />
                    Register for Upcoming Competitions
                  </Link>
                </div>
                
                <div className="hero-trust">
                  <p>Trusted by parents across Kenya • International recognition</p>
                </div>
              </Col>
              
              <Col lg={6} className="hero-visual">
                <div className="hero-image-container">
                  <div className="floating-robot">
                    <FaRobot className="robot-icon" />
                  </div>
                  <div className="code-snippets">
                    <div className="code-snippet snippet-1">
                      <FaCode />
                     <span>{'while(learning) { innovate(); }'}</span>
                    </div>
                    <div className="code-snippet snippet-2">
                      <FaCogs />
                      <span>build.robot().program().compete()</span>
                    </div>
                    <div className="code-snippet snippet-3">
                      <FaLightbulb />
                      <span>if(creativity) return success;</span>
                    </div>
                  </div>
                  <div className="geometric-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                    <div className="shape shape-3"></div>
                  </div>
                </div>
              </Col>
            </Row>
            
            <div className="scroll-indicator">
              <FaChevronDown />
              <span>Discover More</span>
            </div>
          </Container>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <Container>
            <Row>
              {stats.map((stat, index) => (
                <Col key={stat.id} md={6} lg={3}>
                  <div className="stat-card">
                    <div className="stat-number">
                      {activeCounter[stat.id] || 0}{stat.suffix}
                    </div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Programs Section */}
        <section className="programs-section">
          <Container>
            <div className="section-header">
              <div className="section-badge">
                <FaGraduationCap className="me-2" />
                Our Programs
              </div>
              <h2 className="section-title">
                Comprehensive STEM Education
                <span className="gradient-text"> For Every Age</span>
              </h2>
              <p className="section-description">
                From toddlers to teenagers, we have the perfect program to nurture your child's technological potential
              </p>
            </div>
            
            <Row className="g-4">
              {programs.map((program, index) => (
                <Col key={index} md={6} lg={4}>
                  <div className="program-card">
                    <div className="program-icon">
                      {program.icon}
                    </div>
                    <div className="program-content">
                      <h3 className="program-title">{program.title}</h3>
                      <p className="program-description">{program.description}</p>
                      <div className="program-meta">
                        <span className="age-range">{program.ageRange}</span>
                        <span className="program-highlight">{program.highlight}</span>
                      </div>
                    </div>
                    <div className="program-hover-effect"></div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Age Groups Section */}
        <section className="age-groups-section">
          <Container>
            <div className="section-header">
              <div className="section-badge">
                <FaUsers className="me-2" />
                Learning Paths
              </div>
              <h2 className="section-title">
                Age-Appropriate
                <span className="gradient-text"> Learning Journeys</span>
              </h2>
              <p className="section-description">
                Carefully crafted curriculum that grows with your child's development
              </p>
            </div>
            
            <Row className="g-4">
              {ageGroups.map((group, index) => (
                <Col key={index} md={6} lg={3}>
                  <div className="age-group-card">
                    <div className="age-badge">{group.age}</div>
                    <h3 className="age-title">{group.title}</h3>
                    <p className="age-description">{group.description}</p>
                    <ul className="age-features">
                      {group.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <Container>
            <div className="section-header">
              <div className="section-badge">
                <FaMedal className="me-2" />
                Why Choose Us
              </div>
              <h2 className="section-title">
                The Stemtrix
                <span className="gradient-text"> Advantage</span>
              </h2>
              <p className="section-description">
                We're not just teaching technology - we're building future innovators and leaders
              </p>
            </div>
            
            <Row className="g-4">
              <Col md={4}>
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaRocket />
                  </div>
                  <h3>Start Early, Lead Tomorrow</h3>
                  <p>Beginning at age 4, we nurture curiosity and build foundational STEM skills that give your child a competitive advantage in the digital future.</p>
                </div>
              </Col>
              <Col md={4}>
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaLaptopCode />
                  </div>
                  <h3>Hands-On Learning</h3>
                  <p>Students don't just learn about robots - they build, program, and compete with them. Real projects that develop critical thinking and problem-solving skills.</p>
                </div>
              </Col>
              <Col md={4}>
                <div className="feature-card">
                  <div className="feature-icon">
                    <FaGlobe />
                  </div>
                  <h3>Global Recognition</h3>
                  <p>Our students compete in national and international robotics competitions, with winners representing Kenya in Beijing, China, and Michigan, USA.</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section">
          <div className="testimonials-background"></div>
          <Container>
            <div className="section-header">
              <div className="section-badge">
                <FaStar className="me-2" />
                Success Stories
              </div>
              <h2 className="section-title text-white">
                What Parents & Educators
                <span className="gradient-text"> Are Saying</span>
              </h2>
              <p className="section-description text-light">
                Real experiences from families who've seen the Stemtrix transformation
              </p>
            </div>
            
            <div className="testimonial-carousel">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-stars">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                  <blockquote className="testimonial-text">
                    "{testimonials[currentTestimonial].text}"
                  </blockquote>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <strong>{testimonials[currentTestimonial].name}</strong>
                      <span>{testimonials[currentTestimonial].role}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="testimonial-indicators">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${index === currentTestimonial ? 'active' : ''}`}
                    onClick={() => setCurrentTestimonial(index)}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* News Section */}
        <section className="news-section">
          <Container>
            <div className="section-header">
              <div className="section-badge">
                <FaTrophy className="me-2" />
                Latest News
              </div>
              <h2 className="section-title">
                Achievements &
                <span className="gradient-text"> Success Stories</span>
              </h2>
              <p className="section-description">
                Stay updated with our competitions, events, and student accomplishments
              </p>
            </div>
            
            <Row className="g-4">
              <Col xs={12} md={6} lg={3}>
                <article className="news-card">
                  <div className="news-video">
                    <iframe
                      src="https://www.youtube.com/embed/eV7RRlNloZs"
                      title="Inspire Robotics Challenge 2025"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                    <div className="play-overlay">
                      <FaPlay />
                    </div>
                  </div>
                  <div className="news-content">
                    <div className="news-date">March 15, 2025</div>
                    <h3 className="news-title">Inspire Robotics Challenge 2025</h3>
                    <p className="news-excerpt">
                      Schools competed under the "Health" theme. Winners represent Kenya in Beijing, China.
                    </p>
                  </div>
                </article>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <article className="news-card">
                  <div className="news-video">
                    <iframe
                      src="https://www.youtube.com/embed/8P7GzmF8Xf0"
                      title="Robofest at Merishaw School"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                    <div className="play-overlay">
                      <FaPlay />
                    </div>
                  </div>
                  <div className="news-content">
                    <div className="news-date">April 5, 2025</div>
                    <h3 className="news-title">Robofest at Merishaw School</h3>
                    <p className="news-excerpt">
                      Seven schools competed with top teams earning spots at the global event in Michigan.
                    </p>
                  </div>
                </article>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <article className="news-card">
                  <div className="news-video">
                    <iframe
                      src="https://www.youtube.com/embed/cfNnN_UxdpQ"
                      title="Winners of the Inspire Robotics Challenge 2023/2024"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                    <div className="play-overlay">
                      <FaPlay />
                    </div>
                  </div>
                  <div className="news-content">
                    <div className="news-date">February 10, 2024</div>
                    <h3 className="news-title">Inspire Robotics Champions</h3>
                    <p className="news-excerpt">
                      Riara Springs Girls showcased outstanding innovation to claim the championship.
                    </p>
                  </div>
                </article>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <article className="news-card">
                  <div className="news-video">
                    <iframe
                      src="https://www.youtube.com/embed/RKgyaGQUqsE"
                      title="2023-2024 Robotics Season"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                    <div className="play-overlay">
                      <FaPlay />
                    </div>
                  </div>
                  <div className="news-content">
                    <div className="news-date">January 15, 2025</div>
                    <h3 className="news-title">2023-2024 Season Highlights</h3>
                    <p className="news-excerpt">
                      Exciting challenges and innovative problem-solving scenarios from this year's competitions.
                    </p>
                  </div>
                </article>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Final CTA Section */}
        <section className="final-cta-section">
          <div className="cta-background"></div>
          <Container>
            <Row className="align-items-center">
              <Col lg={8}>
                <h2 className="cta-title">
                  Ready to Transform Your Child's Future?
                </h2>
                <p className="cta-description">
                  Join over 500 families who've chosen Stemtrix to prepare their children for tomorrow's challenges. 
                  Starting from age 4, we make learning robotics and coding engaging, accessible, and fun.
                </p>
                <div className="cta-features">
                  <div className="cta-feature">
                    <FaUsers />
                    <span>Small class sizes for personalized attention</span>
                  </div>
                  <div className="cta-feature">
                    <FaTrophy />
                    <span>International competition opportunities</span>
                  </div>
                  <div className="cta-feature">
                    <FaCalendarAlt />
                    <span>Flexible scheduling & holiday programs</span>
                  </div>
                </div>
              </Col>
              <Col lg={4}>
                <div className="cta-actions">
                  <Link to="/join-us" className="cta-btn cta-primary w-100 mb-3">
                    <FaRocket className="me-2" />
                    Enroll Your Child Today
                    <FaArrowRight className="ms-2" />
                  </Link>
                  <Link to="/contact" className="cta-btn cta-secondary w-100">
                    Schedule a Free Trial Class
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* WhatsApp Button */}
        <div className={`whatsapp-button ${isVisible ? 'visible' : ''}`}>
          <a
            href="https://wa.me/+254728128353?text=Hello!%20I%20found%20your%20contact%20on%20the%20Stemtrix%20website%20and%20would%20like%20to%20learn%20more%20about%20your%20robotics%20programs%20for%20my%20child."
            className="whatsapp-link"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Contact us on WhatsApp for robotics program information"
          >
            <div className="whatsapp-tooltip">
              <span>Chat with us!</span>
              <small>Get instant program info</small>
            </div>
            <FaWhatsapp />
          </a>
        </div>
      </main>
    </>
  );
};

export default HomePage;